import { cn } from "@/lib/utils";

export interface ProgressBarProps {
  /** Numerador — lo hecho. */
  valor: number;
  /** Denominador — el universo sobre el que se mide. */
  total: number;
  /**
   * Texto de la proporción. Por defecto `valor/total`. El DS EXIGE que la
   * barra vaya siempre con su proporción escrita al lado: el color acompaña
   * al dato, no lo reemplaza.
   */
  etiqueta?: string;
  /** 6px en fila (por defecto), 7px dentro de una tarjeta. */
  alto?: 6 | 7;
  className?: string;
}

/**
 * Color por umbral: >80% verde, >50% ámbar, resto rojo; sin dato, gris.
 *
 * Exportado a propósito: cuando una pantalla acompaña la barra con OTRA
 * lectura del mismo porcentaje (el número grande de la ficha de capacitación,
 * por ejemplo), tiene que usar este mismo corte. Reimplementar el semáforo en
 * el call site deja el número en verde y la barra en ámbar justo en el borde
 * del umbral, y nadie lo ve hasta que el dato cae exactamente ahí.
 */
export function colorPorUmbral(pct: number, sinDato: boolean): string {
  if (sinDato) return "var(--border-strong)";
  if (pct > 80) return "var(--success-bar)";
  if (pct > 50) return "var(--warning-bar)";
  return "var(--destructive-bar)";
}

/**
 * Barra de progreso del Design System. Nunca la uses sin la proporción: una
 * barra sola no se puede reconciliar con la fila que la origina.
 */
export function ProgressBar({
  valor,
  total,
  etiqueta,
  alto = 6,
  className,
}: ProgressBarProps) {
  const sinDato = !total || total <= 0;
  const pct = sinDato ? 0 : Math.min(100, Math.round((valor / total) * 100));

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <div
        className="bg-border-subtle min-w-0 flex-1 overflow-hidden rounded-full"
        style={{ height: alto }}
        role="progressbar"
        aria-valuenow={valor}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className="h-full rounded-full transition-[width]"
          style={{ width: `${pct}%`, background: colorPorUmbral(pct, sinDato) }}
        />
      </div>
      <span className="fc-num text-muted-foreground shrink-0 text-[11px]">
        {etiqueta ?? `${valor}/${total}`}
      </span>
    </div>
  );
}
